/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';

import { UndoService } from 'src/app/common/undo/undo.service';
import { TicketMoveOperation } from 'src/app/common/undo/ticket-move-operation';
import { TicketDeleteOperation } from 'src/app/common/undo/ticket-delete-operation';
import { TicketEditOperation } from 'src/app/common/undo/ticket-edit-operation';
import { TicketDuplicateOperation } from 'src/app/common/undo/ticket-duplicate-operation';

import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
import { PlanObject } from 'src/app/common/fancy-firebase/objects/PlanObject';
import { PlanState } from 'src/app/services/plan-state.service';
import { monitorPreviousDeletion$ } from 'src/app/common/undo/undo-utils';
import { ClipboardPasteOperation } from 'src/app/common/undo/clipboard-paste-operation';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { TicketEditCommonDesktopService } from '../../common/docked-ticket/services/ticket-edit-common-desktop.service';

/**
 * First cut of this class is basically just to make it possible to go:
 * ```
 * constructor(planUndos: PlanUndosService) {
 *   planUndo.ticketMoveOperation.doThing()
 * }
 * ```
 * To provide access to the operators from anywhere on the plan.
 * I can't think of any good reason this class needs to be partiularly extensible or flexible. So everything is very
 * static and hardcode-y, mainly just for the typescript-y goodness.
 */

@Injectable()
export class PlanUndosService {
	//TODO - enhancement, make readonly
	public ticketEditOperation: TicketEditOperation;
	public ticketMoveOperation: TicketMoveOperation;
	public ticketDeleteOperation: TicketDeleteOperation;
	public ticketDuplicateOperation: TicketDuplicateOperation;
	public clipboardPasteOperation: ClipboardPasteOperation;

	constructor(private undoService: UndoService, private featureFlagManager: FeatureFlagManager) {}

	//pass in args. Don't particularly want this to be flexible. But it's preferable to limit the angular side dependencies
	public setupOperations(
		ticketList: TicketList,
		plan: PlanObject,
		planState: PlanState,
		ticketEditCommonService: TicketEditCommonDesktopService
	) {
		//create here so that all the operations share the same instance. _efficiency_
		const previousDeletions = monitorPreviousDeletion$(ticketList.rawEvent$, (action) =>
			ticketList.isRemoveAction(action)
		);

		this.ticketEditOperation = new TicketEditOperation(
			ticketList,
			planState,
			ticketEditCommonService,
			previousDeletions
		);
		this.ticketMoveOperation = new TicketMoveOperation(ticketList, plan, planState, previousDeletions);
		this.ticketDeleteOperation = new TicketDeleteOperation(ticketList, planState, this.featureFlagManager);
		this.ticketDuplicateOperation = new TicketDuplicateOperation(
			ticketList,
			planState,
			previousDeletions,
			this.featureFlagManager
		);
		this.clipboardPasteOperation = new ClipboardPasteOperation(planState, this.featureFlagManager);

		this.undoService.registerOperation(this.ticketEditOperation);
		this.undoService.registerOperation(this.ticketMoveOperation);
		this.undoService.registerOperation(this.ticketDeleteOperation);
		this.undoService.registerOperation(this.ticketDuplicateOperation);
		this.undoService.registerOperation(this.clipboardPasteOperation);
	}

	public destroyOperations() {
		this.undoService.unRegisterOperation(this.ticketEditOperation);
		this.undoService.unRegisterOperation(this.ticketMoveOperation);
		this.undoService.unRegisterOperation(this.ticketDeleteOperation);
		this.undoService.unRegisterOperation(this.ticketDuplicateOperation);
		this.undoService.unRegisterOperation(this.clipboardPasteOperation);
	}
}
